// RunStoryLegend — THE one canvas legend (v2 design: a single collapsible
// panel in the canvas corner). Two vocabularies, one home:
//   1. the run-story idioms — visited/unvisited nodes, assertion pills,
//      sequence/revisit chips, terminus variants, branch-taken edges;
//   2. the topology vocabulary present in the loaded flow — node types,
//      connection styles, DDD context relationships (data-driven, from the
//      same selectors the old footer Legend strip used before it was
//      consolidated away).
//
// HTML overlay in the canvas corner (same idiom as ZoomControls). Expanded
// by default until the user dismisses it; the collapsed state persists in
// uiSlice for the session.

import { useSelector, useDispatch } from 'react-redux';
import {
  selectRunLegendCollapsed,
  toggleRunLegend,
  selectScenarioStage,
} from '../../store/slices/uiSlice.ts';
import {
  selectLegendEntries,
  selectConnectionLegendEntries,
  selectRelationshipLegendEntries,
} from '../../store/slices/topologySlice.ts';

/** Tiny SVG swatches that mirror the real canvas rendering. */
function NodeSwatch({ dimmed }: { dimmed?: boolean }) {
  return (
    <svg width="22" height="12" viewBox="0 0 22 12" aria-hidden="true">
      <rect
        x="1"
        y="1"
        width="20"
        height="10"
        rx="3"
        fill="var(--node-fill)"
        fillOpacity={dimmed ? 0.3 : 0.55}
        stroke={dimmed ? 'var(--text-dim)' : 'var(--node-stroke-visited)'}
        strokeOpacity={dimmed ? 0.4 : 1}
        strokeDasharray={dimmed ? '3,2' : 'none'}
      />
    </svg>
  );
}

function PillSwatch({ kind }: { kind: 'pass' | 'skip' | 'fail' }) {
  const isFail = kind === 'fail';
  const isSkip = kind === 'skip';
  const color = isFail ? 'var(--red)' : isSkip ? 'var(--text-muted)' : 'var(--green)';
  return (
    <svg width="20" height="13" viewBox="0 0 20 13" aria-hidden="true">
      <rect
        x="1"
        y="1"
        width="18"
        height="11"
        rx={isFail ? 2 : 5.5}
        fill={isFail ? 'var(--red)' : isSkip ? 'transparent' : 'rgba(74, 222, 128, 0.16)'}
        stroke={isFail ? 'none' : color}
        strokeWidth="1"
        strokeDasharray={isSkip ? '2,1.5' : 'none'}
      />
      <text
        x="10"
        y="7.5"
        textAnchor="middle"
        dominantBaseline="middle"
        fill={isFail ? '#fff' : color}
        fontSize="7"
        fontWeight="700"
        fontFamily="var(--mono)"
      >
        {isFail ? '✗n' : isSkip ? '—n' : '✓n'}
      </text>
    </svg>
  );
}

function ChipSwatch() {
  return (
    <svg width="26" height="13" viewBox="0 0 26 13" aria-hidden="true">
      <rect
        x="1"
        y="1"
        width="24"
        height="11"
        rx="3"
        fill="var(--bg-pill)"
        stroke="rgba(129, 140, 248, 0.45)"
      />
      <text
        x="13"
        y="7.5"
        textAnchor="middle"
        dominantBaseline="middle"
        fill="var(--indigo)"
        fontSize="7"
        fontWeight="700"
        fontFamily="var(--mono)"
      >
        4↩12
      </text>
    </svg>
  );
}

function TerminusSwatch({ variant }: { variant: 'end' | 'stop' | 'aborted' }) {
  const color = variant === 'end' ? 'var(--green)' : variant === 'stop' ? '#fb923c' : 'var(--red)';
  const text = variant === 'end' ? '⏹ END' : variant === 'stop' ? '⛔ STOP' : '⚠ ABORT';
  return (
    <svg width="44" height="15" viewBox="0 0 44 15" aria-hidden="true">
      {variant === 'stop' ? (
        <polygon
          points="5,1 39,1 43,5 43,10 39,14 5,14 1,10 1,5"
          fill="rgba(251, 146, 60, 0.15)"
          stroke={color}
          strokeWidth="1.2"
        />
      ) : (
        <rect
          x="1"
          y="1"
          width="42"
          height="13"
          rx="6.5"
          fill={variant === 'end' ? 'rgba(74, 222, 128, 0.13)' : 'rgba(239, 68, 68, 0.13)'}
          stroke={color}
          strokeDasharray={variant === 'aborted' ? '4,3' : 'none'}
        />
      )}
      <text
        x="22"
        y="8.5"
        textAnchor="middle"
        dominantBaseline="middle"
        fill={color}
        fontSize="7"
        fontWeight="700"
        fontFamily="var(--mono)"
      >
        {text}
      </text>
    </svg>
  );
}

function EdgeSwatch() {
  return (
    <svg width="22" height="10" viewBox="0 0 22 10" aria-hidden="true">
      <line x1="0" y1="5" x2="18" y2="5" stroke="var(--green)" strokeWidth="1.5" opacity="0.8" />
      <circle cx="19" cy="5" r="2.5" fill="var(--green)" opacity="0.9" />
    </svg>
  );
}

export default function RunStoryLegend() {
  const dispatch = useDispatch();
  const collapsed = useSelector(selectRunLegendCollapsed);
  const stage = useSelector(selectScenarioStage);
  const nodeEntries = useSelector(selectLegendEntries);
  const connectionEntries = useSelector(selectConnectionLegendEntries);
  const relationshipEntries = useSelector(selectRelationshipLegendEntries);

  if (collapsed) {
    return (
      <div className="run-legend run-legend-collapsed" data-testid="run-story-legend">
        <button
          className="run-legend-toggle"
          onClick={() => dispatch(toggleRunLegend())}
          aria-expanded={false}
          aria-label="Expand run story legend"
        >
          <span className="run-legend-title">LEGEND</span>
          <span className="run-legend-caret">▸</span>
        </button>
      </div>
    );
  }

  return (
    <div
      className="run-legend"
      role="region"
      aria-label="Run story legend"
      data-testid="run-story-legend"
    >
      <div className="run-legend-head">
        <span className="run-legend-title">LEGEND</span>
        <span className="run-legend-stage">
          {stage === 2 ? 'STAGE ② — STORY & PROOF' : 'STAGE ① — THE MAP'}
        </span>
        <button
          className="run-legend-toggle"
          onClick={() => dispatch(toggleRunLegend())}
          aria-expanded={true}
          aria-label="Collapse run story legend"
        >
          –
        </button>
      </div>
      <div className="run-legend-body">
        <div className="run-legend-row">
          <NodeSwatch />
          <span>visited</span>
          <NodeSwatch dimmed />
          <span>unvisited</span>
        </div>
        <div className="run-legend-row">
          <PillSwatch kind="pass" />
          <span>passed</span>
          <PillSwatch kind="skip" />
          <span>skipped</span>
          <PillSwatch kind="fail" />
          <span>failed</span>
        </div>
        <div className="run-legend-row">
          <ChipSwatch />
          <span>step order (↩ revisit)</span>
        </div>
        <div className="run-legend-row">
          <TerminusSwatch variant="end" />
          <span>terminal end</span>
        </div>
        <div className="run-legend-row">
          <TerminusSwatch variant="stop" />
          <span>expected stop</span>
        </div>
        <div className="run-legend-row">
          <TerminusSwatch variant="aborted" />
          <span>run aborted</span>
        </div>
        <div className="run-legend-row">
          <EdgeSwatch />
          <span>branch taken</span>
        </div>

        {/* Topology vocabulary — data-driven from the loaded flow. */}
        {nodeEntries.length > 0 && (
          <div className="run-legend-group" role="group" aria-label="Node types">
            <div className="run-legend-group-head">NODES</div>
            {nodeEntries.map(({ type, style }) => (
              <div key={type} className="run-legend-row" role="listitem">
                <span className="run-legend-glyph" style={{ color: style.fill }}>
                  {style.icon}
                </span>
                <span>{style.label}</span>
              </div>
            ))}
          </div>
        )}
        {connectionEntries.length > 0 && (
          <div className="run-legend-group" role="group" aria-label="Connection styles">
            <div className="run-legend-group-head">CONNECTIONS</div>
            {connectionEntries.map(({ style, def, label }) => (
              <div key={style} className="run-legend-row">
                <svg width="18" height="10" viewBox="0 0 18 10" aria-hidden="true">
                  <line
                    x1="0"
                    y1="5"
                    x2="18"
                    y2="5"
                    stroke={def.color}
                    strokeWidth={1.5}
                    strokeDasharray={def.dash ? '4,2' : 'none'}
                  />
                </svg>
                <span>{label}</span>
              </div>
            ))}
          </div>
        )}
        {relationshipEntries.length > 0 && (
          <div className="run-legend-group" role="group" aria-label="Context relationships">
            <div className="run-legend-group-head">CONTEXT TRANSITIONS</div>
            {relationshipEntries.map(({ type, abbrev, label }) => (
              <div key={type} className="run-legend-row">
                <span className="run-legend-rel-pill">{abbrev}</span>
                <span>{label}</span>
              </div>
            ))}
          </div>
        )}

        <div className="run-legend-note">
          {stage === 2
            ? 'The walk is the story and the proof at once: numbered steps, turns taken, an ending with a reason — and every lit node shows its receipts. Click one.'
            : 'Shapes carry meaning before color does. A scenario’s kind is what it tests; the verdict is how the proof went.'}
        </div>
      </div>
    </div>
  );
}
